import { cache } from '../cache-impl/cache-registry';
import { constructAsync } from '../../../webmodule-common/other/async-constructor';
import { currentQuoteSupplierProvider } from '../../quotes/data/quoteSupplierProvider';
import { DevelopmentError } from '../../../webmodule-common/other/development-error';
import { FranchiseeQuoteContainer } from './data/franchisee-quote-container';
import { FranchiseeQuoteContainerManager } from './data/franchisee-quote-manager';
import { FranchiseeV6QuoteView } from './views/v6/franchisee-v6-quote-view';
import { getApiFactory } from '../../api/api-injector';
import { getQuoteTitle } from '../../quotes/data/quote-helper-functions';
import { LangQuoteUiAdapter } from '../../../localization/scripts/dealer-franchisee/quotes/quote-ui-adapter-lang';
import { LockResourceResult } from '../../../webmodule-common/other/resource-lock-info';
import { QuoteContainer, QuoteContainerManager } from '../../quotes/data/quote-container';
import { QuoteView, QuoteViewOptions } from '../../quotes/views/quote-view';
import { ResourceLocker } from '../common/resource-lock';
import { UiFranchiseeQuoteUserInteraction } from './views/franchisee-quote-user-interaction-ui';
import { userDataStore } from '../common/current-user-data-store';
import { waitPatiently } from '../../../webmodule-common/other/ui/modal-spinner';

export class QuoteLock extends ResourceLocker {
  constructor(quoteId: string, lockRevokedEvent: () => Promise<void>, concurrencyCheckEvent?: () => Promise<boolean>) {
    super(quoteId, 'quote', 'quote', lockRevokedEvent, concurrencyCheckEvent);
  }
}

export function getQuoteContainer(id: string): QuoteContainer {
  return new FranchiseeQuoteContainer(id, null, null, null, null, null, null, null, null, null);
}

export function getQuoteContainerManager(
  id: string | QuoteContainer,
  lockInfo?: LockResourceResult
): QuoteContainerManager {
  const container = id instanceof QuoteContainer ? id : getQuoteContainer(id);

  const manager = new FranchiseeQuoteContainerManager(
    container,
    getApiFactory().quote(),
    getApiFactory().blob(),
    getApiFactory().franchisee(),
    new UiFranchiseeQuoteUserInteraction()
  );

  manager.lockInfo = lockInfo;
  return manager;
}

export async function quoteViewFactory(options: QuoteViewOptions): Promise<QuoteView> {
  await options.quoteContainerManager.needsQuote();
  if (options.quoteContainerManager.isV6) return await constructAsync(new FranchiseeV6QuoteView(options));
  else throw new DevelopmentError(`unsupported provider ${currentQuoteSupplierProvider}`);
}

export async function createQuoteView(containerManager: QuoteContainerManager): Promise<QuoteView> {
  let quoteEditor: QuoteView | null = null;

  const waiting = waitPatiently(
    () => LangQuoteUiAdapter.openingQuote(),
    () => LangQuoteUiAdapter.pleaseWaitWhileInitializingQuote()
  );
  try {
    quoteEditor = await quoteViewFactory({
      title: getQuoteTitle, // not used in subclasses
      quoteContainerManager: containerManager,
      customerCache: cache().client,
      userCache: cache().userProfile,
      ownerId: async () => userDataStore.defaultBranch.id
    });
    await waiting.hideModal();
    return quoteEditor;
  } finally {
    await waiting.hideModal();
  }
}
